import {
	SECTIONS,
	type SectionKey,
} from '#app/routes/mocra+/_sections+/section-data.ts'

export type TemplateLink = (typeof SECTIONS)['overview']['templates'][number]

export type SectionResources = {
	readonly mocraSections: ReadonlyArray<SectionKey>
	readonly templates: ReadonlyArray<TemplateLink>
}

function dedupeSectionKeys(keys: ReadonlyArray<SectionKey>): SectionKey[] {
	const seen = new Set<SectionKey>()
	const result: SectionKey[] = []
	for (const key of keys) {
		if (seen.has(key)) continue
		seen.add(key)
		result.push(key)
	}
	return result
}

export function collectTemplates(
	sectionKeys: ReadonlyArray<SectionKey>,
): TemplateLink[] {
	const seen = new Set<string>()
	const templates: TemplateLink[] = []
	for (const key of sectionKeys) {
		const section = SECTIONS[key]
		for (const template of section.templates) {
			if (seen.has(template.filename)) continue
			seen.add(template.filename)
			templates.push(template)
		}
	}
	return templates
}

export function createSectionResources({
	mocraSections,
	templateSections,
}: {
	mocraSections: ReadonlyArray<SectionKey>
	templateSections: ReadonlyArray<SectionKey>
}): SectionResources {
	return {
		mocraSections: dedupeSectionKeys(mocraSections),
		templates: collectTemplates(templateSections),
	}
}

function normalizeRoute(route: string): string {
	const trimmed = route.trim()
	if (trimmed.length === 0) return '/mocra'
	if (trimmed.startsWith('/mocra/')) return trimmed
	const withoutLeadingSlash = trimmed.startsWith('/')
		? trimmed.slice(1)
		: trimmed
	return `/mocra/${withoutLeadingSlash}`
}

export function collectMocraSectionsForRoute(route: string): SectionKey[] {
	const normalizedRoute = normalizeRoute(route)
	const sectionKeys = Object.keys(SECTIONS) as SectionKey[]
	const matches: SectionKey[] = []
	for (const key of sectionKeys) {
		const intakeSections = SECTIONS[key].intakeSections
		const hasMatch = intakeSections.some(
			(section) => section.to === normalizedRoute,
		)
		if (hasMatch) {
			matches.push(key)
		}
	}
	return matches
}

export function createRouteSectionResources({
	route,
	extraMocraSections = [],
	templateSections,
}: {
	route: string
	extraMocraSections?: ReadonlyArray<SectionKey>
	templateSections?: ReadonlyArray<SectionKey>
}): SectionResources {
	const fromRoute = collectMocraSectionsForRoute(route)
	const mocraSections = dedupeSectionKeys([...fromRoute, ...extraMocraSections])
	const templateSource =
		templateSections && templateSections.length > 0
			? templateSections
			: mocraSections
	return createSectionResources({
		mocraSections,
		templateSections: dedupeSectionKeys(templateSource),
	})
}
